/**
 * Índice de Configuración.
 *
 * Antes esto era una carpeta desplegable del menú lateral. El problema de una
 * carpeta es que sólo enseña nombres: para saber si el dominio está puesto o si
 * Stripe está conectado había que entrar en cada una y volver. Como página, cada
 * entrada puede decir en qué estado está, que es la pregunta que trae aquí a la
 * gente la mayoría de las veces.
 *
 * Las entradas siguen siendo secciones de pleno derecho —cada una con su
 * dirección—, así que esto es un índice y no un contenedor: entrar por aquí y
 * entrar por un enlace directo llevan exactamente al mismo sitio.
 */
import { useEffect, useState } from 'react'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { getIntegrations, getStripeConnection, type ConnectStatus, type IntegrationState } from '../../../lib/api'
import type { ConfigAttention } from '../../../lib/configAttention'
import { StripeMark } from './StripeMark'
import { GoogleAnalyticsMark } from './GoogleAnalyticsMark'
import { OmnisendMark } from './OmnisendMark'
import { OpenTableMark } from './OpenTableMark'
import { ADMIN_ONLY_SECTIONS, type WorkspaceSection } from '../WorkspaceSidebar'
import { canOpenSection, type ProjectAccessRole } from '@webbuilder/shared-types'
import { SitePublishState } from './SitePublishState'
import { useI18n } from '../../../lib/i18n'
import {
  IconAlertTriangle,
  IconArchive,
  IconArrowUpRight,
  IconBuildingStore,
  IconCheck,
  IconFileText,
  IconGauge,
  IconSettings,
  IconWorld,
  IconWorldPin,
  IconSend,
} from '@tabler/icons-react'
import { GoogleCalendarLogo } from './GoogleCalendarMark'

type Entry = {
  id: WorkspaceSection
  /** El rótulo sale de `section.*`: el mismo que escribe el migajero. */
  descriptionKey: string
  icon: React.ReactNode
  brand?: 'stripe' | 'google' | 'omnisend' | 'opentable'
}

type Group = {
  titleKey: string
  entries: Entry[]
}

/**
 * Los tres grupos, y por qué son tres.
 *
 * Antes era una lista sola de siete tarjetas bajo el rótulo «Proyecto». Siete
 * es justo el número al que una lista deja de leerse de un vistazo y hay que
 * recorrerla entera para encontrar algo — y encima mezclaba cosas que no se
 * buscan en el mismo momento: quien viene a poner su RFC no viene a mirar
 * backups.
 *
 * El corte no es por tema sino por QUÉ se está tocando:
 *
 *   - «Tu sitio» es el artefacto: la dirección donde vive, cómo se llama, sus
 *     copias y lo que lleva gastado del plan. Se toca al montar o mantener.
 *   - «Tu negocio» es la empresa detrás: quién eres, dónde operas, qué prometes
 *     por escrito. Se toca una vez al principio, y los tres se necesitan a la
 *     vez porque los datos y la región son de donde salen las políticas.
 *   - «Integraciones» son servicios de fuera. Ya estaba separado y se queda.
 */
const GROUPS: Group[] = [
  {
    titleKey: 'config.group.site',
    entries: [
      { id: 'publish', descriptionKey: 'config.desc.publish', icon: <IconWorld size={17} /> },
      { id: 'backups', descriptionKey: 'config.desc.backups', icon: <IconArchive size={17} /> },
      { id: 'usage', descriptionKey: 'config.desc.usage', icon: <IconGauge size={17} /> },
      { id: 'general', descriptionKey: 'config.desc.general', icon: <IconSettings size={17} /> },
      /* La última de «Tu sitio», y a propósito la última: es la que deja de
         tenerlo. Sale de dentro de «General», donde vivía en el mismo bloque
         que borrar el proyecto — entregarlo no es una variante de destruirlo, y
         apilarlas confundía dos decisiones que no se parecen en nada salvo en
         que las dos terminan. Sólo la ve el dueño: `canOpenSection` la ata a
         `project.danger`. */
      { id: 'transfer', descriptionKey: 'config.desc.transfer', icon: <IconSend size={17} /> },
    ],
  },
  {
    titleKey: 'config.group.business',
    entries: [
      { id: 'business', descriptionKey: 'config.desc.business', icon: <IconBuildingStore size={17} /> },
      /* Antes de Políticas y justo después de los datos del negocio: es el ajuste
         del que cuelgan los dos —la moneda que aparece en los términos y la ley que
         citan las plantillas—, así que se encuentra viniendo de ellos. */
      { id: 'region', descriptionKey: 'config.desc.region', icon: <IconWorldPin size={17} /> },
      /* Aquí y no en Integraciones: una integración es un servicio de fuera que se
         conecta —Stripe, Google—, y estos documentos son del propio negocio. */
      { id: 'policies', descriptionKey: 'config.desc.policies', icon: <IconFileText size={17} /> },
    ],
  },
  {
    titleKey: 'config.group.integrations',
    entries: [
      { id: 'stripe', descriptionKey: 'config.desc.stripe', icon: <StripeMark size={22} color="#635BFF" />, brand: 'stripe' },
      { id: 'omnisend', descriptionKey: 'config.desc.omnisend', icon: <OmnisendMark size={22} />, brand: 'omnisend' },
      { id: 'googleAnalytics', descriptionKey: 'config.desc.googleAnalytics', icon: <GoogleAnalyticsMark size={22} />, brand: 'google' },
      /* La puerta a la sincronización de Reservas: se configura allí, junto al
         horario de cada persona, pero aquí es donde se busca. */
      { id: 'calendars', descriptionKey: 'config.desc.calendars', icon: <GoogleCalendarLogo size={22} />, brand: 'google' },
      /* Para restaurantes: conectarlo aquí deja listos todos sus widgets. */
      { id: 'openTable', descriptionKey: 'config.desc.openTable', icon: <OpenTableMark size={30} />, brand: 'opentable' },
    ],
  },
]

/** Lo que dice la pastilla de Stripe en el índice, sin entrar a la ficha. */
const STRIPE_HINT: Record<ConnectStatus, { key: string; tone: 'idle' | 'warn' | 'ok' }> = {
  none: { key: 'config.stripe.none', tone: 'idle' },
  pending: { key: 'config.stripe.pending', tone: 'warn' },
  restricted: { key: 'config.stripe.restricted', tone: 'warn' },
  ready: { key: 'config.stripe.ready', tone: 'ok' },
}

export function ConfigSection({ projectId, token, isAdmin, projectRole = 'owner', attention, publishedSubdomain, onOpenEditor, onUnpublished, onNavigate }: {
  projectId: string
  token: string
  /** Stripe es de administrador, igual que la sección de ventas de la que sale. */
  isAdmin: boolean
  /**
   * Qué eres en este proyecto.
   *
   * `isAdmin` es el rol en véltica —staff o no—; éste es el del proyecto, y son
   * cosas distintas. El índice se filtraba sólo por el primero, así que un
   * invitado veía aquí las mismas puertas que el dueño y sólo se enteraba al
   * pulsarlas, cuando el servidor contestaba «tu rol no permite esta acción».
   */
  projectRole?: ProjectAccessRole
  /** Para la fila de estado de arriba: `null` = el sitio no está publicado. */
  publishedSubdomain?: string | null | undefined
  onOpenEditor?: (() => void) | undefined
  onUnpublished?: (() => void) | undefined
  /**
   * Lo que queda por rellenar, para avisarlo desde el índice.
   *
   * Son avisos de «esto está a medias», no errores: quien entra a Configuración
   * no viene buscando sus políticas, así que si no se le dice aquí no se entera
   * de que su sitio publica un aviso de privacidad con huecos —o ninguno—.
   *
   * Llega del taller y no se consulta aquí porque el menú de la izquierda pinta
   * su aviso con lo mismo: dos consultas podían contestar distinto y dejar el
   * menú avisando de algo que esta pantalla daba por completo.
   */
  attention: ConfigAttention
  onNavigate: (section: WorkspaceSection) => void
}) {
  /* El estado de Stripe se consulta sólo para la pastilla del índice. Si falla
     no se dice nada: una pastilla es una ayuda, y equivocarse en ella es peor
     que no ponerla — mandaría a reconectar una cuenta que ya está conectada. */
  const { t } = useI18n()
  const [stripeStatus, setStripeStatus] = useState<ConnectStatus | null>(null)
  /* Y el de Omnisend, por lo mismo. Aquí importa sobre todo el caso feo: una
     clave revocada deja de mandar contactos sin que nada más lo diga, así que
     la pastilla es el único sitio donde eso se ve sin entrar a la ficha. */
  const [omnisend, setOmnisend] = useState<IntegrationState | null>(null)
  /* Nulos mientras no se sabe: la pastilla no aparece hasta que hay respuesta,
     porque una que parpadea diciendo primero «todo bien» y luego «te falta» es
     peor que llegar medio segundo tarde. */
  const pendingPolicies = attention.policies
  const pendingBusiness = attention.business?.missing ?? null

  /* Quien puede abrir Stripe en este proyecto, no sólo el staff: `isAdmin` es
     el rol en véltica, y Stripe dejó de ser de administrador —ver
     `ADMIN_ONLY_SECTIONS`—. Con `isAdmin` aquí, el dueño no veía ni la
     pastilla ni las filas de su propia cuenta. */
  const canSeeStripe = (isAdmin || !ADMIN_ONLY_SECTIONS.includes('stripe')) && canOpenSection(projectRole, 'stripe')

  useEffect(() => {
    if (!canSeeStripe) return
    let alive = true
    getStripeConnection(projectId, token)
      .then((connection) => { if (alive) setStripeStatus(connection.status) })
      .catch(() => { /* sin pastilla */ })
    return () => { alive = false }
  }, [projectId, token, canSeeStripe])

  /* Omnisend está disponible temporalmente solo para administradores. */
  useEffect(() => {
    if (!isAdmin) { setOmnisend(null); return }
    let alive = true
    getIntegrations(projectId, token)
      .then((integrations) => { if (alive) setOmnisend(integrations.omnisend ?? null) })
      .catch(() => { /* sin pastilla */ })
    return () => { alive = false }
  }, [projectId, token, isAdmin])

  /**
   * Qué pastilla lleva cada entrada, si lleva alguna.
   *
   * `count` es lo único que se escribe; el resto lo dice el icono. Un rótulo
   * como «Faltan 6 datos» ocupaba media línea del título en una tarjeta que ya
   * explica de qué va debajo, y son ocho tarjetas: leídas en columna, ocho
   * frases distintas pesan más que las ocho cosas que hay que hacer. El texto
   * sigue estando en `label`, que va al `title` y al lector de pantalla.
   */
  const hintFor = (entry: Entry): { label: string; tone: 'idle' | 'warn' | 'ok'; count?: number } | null => {
    if (entry.id === 'stripe') {
      if (!stripeStatus) return null
      const hint = STRIPE_HINT[stripeStatus]
      return { label: t(hint.key), tone: hint.tone }
    }

    if (entry.id === 'omnisend') {
      if (!omnisend?.connected) return null
      /* Conectada pero rechazada es el estado que hay que gritar: los contactos
         no están llegando y nada más lo dice. */
      return omnisend.status === 'invalid'
        ? { label: t('config.omnisend.invalid'), tone: 'warn' }
        : { label: t('config.omnisend.ok'), tone: 'ok' }
    }

    if (entry.id === 'policies' && pendingPolicies !== null) {
      const { unwritten, holes } = pendingPolicies
      // Lo que falta por escribir manda sobre lo que falta por rellenar: no
      // tener el documento es más grave que tenerlo a medias.
      if (unwritten > 0) {
        return {
          label: unwritten === 4 ? t('config.policies.noneWritten') : t('config.policies.unwritten', { count: unwritten }),
          tone: 'warn',
          count: unwritten,
        }
      }
      if (holes > 0) {
        return {
          label: holes === 1 ? t('config.policies.holesOne') : t('config.policies.holesMany', { count: holes }),
          tone: 'warn',
          count: holes,
        }
      }
      return { label: t('config.policies.complete'), tone: 'ok' }
    }

    if (entry.id === 'business' && pendingBusiness !== null) {
      if (pendingBusiness === 0) return { label: t('config.business.complete'), tone: 'ok' }
      return { label: t('config.business.missing', { count: pendingBusiness }), tone: 'warn', count: pendingBusiness }
    }

    return null
  }

  const renderEntry = (entry: Entry) => {
    const hint = hintFor(entry)
    return (
      <li key={entry.id}>
        <button type="button" className="dash-config-card" onClick={() => onNavigate(entry.id)}>
          <span className={`dash-widget-glass-icon dash-sidebar-glass-icon dash-config-card-icon${entry.brand ? ` is-brand is-${entry.brand}` : ' is-neutral'}`} aria-hidden="true">
            {entry.icon}
          </span>
          <span className="dash-config-card-copy">
            <span className="dash-config-card-title-row">
              <strong>{t(`section.${entry.id}`)}</strong>
              {hint ? (
                <span className={`dash-connect-status dash-config-hint is-${hint.tone}`} title={hint.label}>
                  {hint.tone === 'ok'
                    ? <IconCheck size={13} aria-hidden="true" />
                    : <IconAlertTriangle size={13} aria-hidden="true" />}
                  {hint.count ? <span aria-hidden="true">{hint.count}</span> : null}
                  <span className="dash-sr-only">{hint.label}</span>
                </span>
              ) : null}
            </span>
            <span className="dash-config-card-description">{t(entry.descriptionKey)}</span>
          </span>
          <span className="dash-secondary-btn dash-summary-action dash-config-card-open">
            {t('config.open')} <IconArrowUpRight size={13} aria-hidden="true" />
          </span>
        </button>
      </li>
    )
  }

  /* Se esconde lo que este usuario no puede abrir, y un grupo puede quedarse
     vacío: se filtra antes de pintar para no dejar un rótulo suelto sin nada
     debajo.

     La lista de qué es de administrador sale de `ADMIN_ONLY_SECTIONS`, la misma
     que usa la guarda de navegación, y no de una condición escrita a mano aquí.
     Estaba escrita a mano —sólo `stripe`— y por eso Backups, que también es de
     administrador, se ofrecía a todo el mundo: al pulsarla la guarda devolvía a
     Inicio sin decir por qué. Un índice que ofrece una puerta cerrada es peor
     que uno que no la enseña. */
  const groups = GROUPS
    .map((group) => ({
      ...group,
      entries: group.entries.filter((entry) => (
        (isAdmin || !ADMIN_ONLY_SECTIONS.includes(entry.id))
        /* Y lo mismo por el rol del PROYECTO. Sale de `canOpenSection`, la misma
           función que filtra el menú lateral y que decide a qué capacidad
           corresponde cada sección en el servidor: así el índice, el menú y la
           reja no pueden acabar diciendo tres cosas distintas. */
        && canOpenSection(projectRole, entry.id)
      )),
    }))
    .filter((group) => group.entries.length > 0)

  return (
    <ProjectSettingsLayout
      title={t('config.title')}
      description={t('config.intro')}
    >
      {/* Lo primero, pero SÓLO si el sitio no está en línea.
          Sin publicar manda sobre todo lo que hay debajo —ni las direcciones
          responden ni las páginas de reserva existen—, y la fila lleva al editor
          a publicar. En línea no se enseña: «está en línea» es lo normal y no
          hace falta decirlo en cada visita, y el botón de retirarlo quedaba en
          primer plano como la acción principal de Configuración. Despublicar
          sigue en el menú de Publicar del editor.
          `undefined` es «aún no se sabe» y tampoco se enseña: no queremos que la
          fila aparezca y desaparezca mientras carga. */}
      {publishedSubdomain === null && canOpenSection(projectRole, 'publish') ? (
        <SitePublishState
          projectId={projectId}
          token={token}
          publishedSubdomain={publishedSubdomain}
          {...(onOpenEditor ? { onOpenEditor } : {})}
          {...(onUnpublished ? { onUnpublished } : {})}
        />
      ) : null}

      {groups.map((group) => (
        <section className="dash-config-group" key={group.titleKey}>
          <h2 className="dash-config-group-title">{t(group.titleKey)}</h2>
          <ul className="dash-config-list">{group.entries.map(renderEntry)}</ul>
        </section>
      ))}
    </ProjectSettingsLayout>
  )
}
